// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  apiFetchInit,
} from "@/legacy/helpersModule";
import {
  MANDATORY_OPENING_DEFAULTS,
  MANDATORY_CLOSING_DEFAULTS,
} from "@/constants/interviewQuestions";
import { Button, Field, Input, Select, Textarea } from "@/shared/components/ui";
import { Modal } from "@/shared/components/ui/Modal";
import { useEscapeKey } from "@/shared/hooks/useEscapeKey";
export function JobMaster({ jobs, onSave, onSaveNow, onBack }) {
  /* Persist to the server immediately and report success. Falls back to the
   * in-memory update if no server-save handler was provided. */
  const persistNow = async (nextJobs) => {
    if (typeof onSaveNow === "function") return onSaveNow(nextJobs);
    onSave(nextJobs);
    return { ok: true };
  };
  const MAX_ROLE_Q = 20;
  const MAX_OPEN_Q = 10;
  const MAX_CLOSE_Q = 5;
  const phaseCap = (phase) => {
    if (phase === "mandatory_open") return MAX_OPEN_Q;
    if (phase === "mandatory_close") return MAX_CLOSE_Q;
    return MAX_ROLE_Q;
  };
  const phaseLabel = (phase) => {
    if (phase === "mandatory_open") return "opening";
    if (phase === "mandatory_close") return "closing";
    return "role";
  };
  const defaultNewQuestionRows = () => [
    ...MANDATORY_OPENING_DEFAULTS.map((question) => ({ question, questionType: "open_ended", questionPhase: "mandatory_open" })),
    ...Array.from({ length: 5 }, () => ({ question: "", questionType: "open_ended", questionPhase: "role" })),
    ...MANDATORY_CLOSING_DEFAULTS.map((question) => ({ question, questionType: "open_ended", questionPhase: "mandatory_close" })),
  ];
  const [local, setLocal] = useState(jobs);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ title: "", designation: "", location: "", description: "", requirements: "" });
  const [questionRows, setQuestionRows] = useState(defaultNewQuestionRows);
  const [qErrors, setQErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [removingId, setRemovingId] = useState(null);
  const [jobSearch, setJobSearch] = useState("");
  const [positionToggling, setPositionToggling] = useState(false);
  const [positionConfirm, setPositionConfirm] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const editorRef = useRef(null);
  const editingJob =
    edit && edit !== "new" ? local.find((j) => j.id === edit) : null;
  const positionOpen = editingJob ? editingJob.isOpen !== false : true;
  const filteredJobs = useMemo(() => {
    const q = jobSearch.trim().toLowerCase();
    if (!q) return local;
    return local.filter((j) => {
      const hay = [j.title, j.designation, j.location]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [local, jobSearch]);
  // When the editor opens (Edit or + New), bring the form into view so the user
  // doesn't have to scroll up from a job lower in the list.
  useEffect(() => {
    if (edit) editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [edit]);
  // Esc leaves Job Master, persisting the current jobs first (same as ← Back).
  // When the editor form is open, Esc just closes the form instead.
  useEscapeKey(() => {
    if (edit) { setEdit(null); return; }
    void (async () => { await persistNow(local); onBack(); })();
  });
  const normalizeRowsFromJob = (j) => {
    const phaseOrder = { mandatory_open: 0, role: 1, mandatory_close: 2 };
    const iq = (j.interviewQuestions || []).slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    const mapped = iq
      .map((q) => ({
        question: q.question || "",
        questionType: ["open_ended", "yes_no", "scale_1_5"].includes(q.questionType) ? q.questionType : "open_ended",
        questionPhase: ["mandatory_open", "role", "mandatory_close"].includes(q.questionPhase) ? q.questionPhase : "role",
        id: q.id,
      }))
      .sort((a, b) => (phaseOrder[a.questionPhase] ?? 1) - (phaseOrder[b.questionPhase] ?? 1));
    if (mapped.length === 0) return defaultNewQuestionRows();
    const hasEmptyRole = mapped.some((r) => r.questionPhase === "role" && !r.question.trim());
    if (!hasEmptyRole) return [...mapped, { question: "", questionType: "open_ended", questionPhase: "role" }];
    return mapped;
  };
  const removeJob = (job) => {
    if (!job || !job.id) return;
    setRemoveConfirm({ job, label: job.title || "this job" });
  };
  const confirmRemoveJob = async (job, label) => {
    setRemoveConfirm(null);
    setRemovingId(job.id);
    try {
      const res = await fetch(
        `/api/jobs/${encodeURIComponent(job.id)}`,
        apiFetchInit({ method: "DELETE" }),
      );
      if (!res.ok && res.status !== 404) {
        const data = await res.json().catch(() => ({}));
        setToast(data.error || `Could not remove job (${res.status}).`);
        setTimeout(() => setToast(""), 4000);
        return;
      }
      const next = local.filter((x) => x.id !== job.id);
      setLocal(next);
      onSave(next);
      if (edit === job.id) setEdit(null);
      setToast(`Removed "${label}".`);
      setTimeout(() => setToast(""), 4000);
    } catch (_e) {
      setToast("Network error — could not remove job.");
      setTimeout(() => setToast(""), 4000);
    } finally {
      setRemovingId(null);
    }
  };
  const saveJob = async () => {
    if (!form.title.trim()) return;
    const errs = {};
    const filled = [];
    questionRows.forEach((row, i) => {
      const t = row.question.trim();
      if (!t) return;
      if (t.length < 10 || t.length > 500) errs[i] = "Min 10 characters, max 500.";
      filled.push({
        question: t,
        questionType: ["open_ended", "yes_no", "scale_1_5"].includes(row.questionType) ? row.questionType : "open_ended",
        questionPhase: ["mandatory_open", "role", "mandatory_close"].includes(row.questionPhase) ? row.questionPhase : "role",
      });
    });
    setQErrors(errs);
    if (Object.keys(errs).length) return;
    const roleN = filled.filter((q) => q.questionPhase === "role").length;
    const openN = filled.filter((q) => q.questionPhase === "mandatory_open").length;
    const closeN = filled.filter((q) => q.questionPhase === "mandatory_close").length;
    if (roleN > MAX_ROLE_Q) {
      setToast(`Maximum ${MAX_ROLE_Q} role-specific questions.`);
      setTimeout(() => setToast(""), 4000);
      return;
    }
    if (openN > MAX_OPEN_Q) {
      setToast(`Maximum ${MAX_OPEN_Q} mandatory opening questions.`);
      setTimeout(() => setToast(""), 4000);
      return;
    }
    if (closeN > MAX_CLOSE_Q) {
      setToast(`Maximum ${MAX_CLOSE_Q} mandatory closing questions.`);
      setTimeout(() => setToast(""), 4000);
      return;
    }
    const interviewQuestions = filled.map((q, idx) => ({ ...q, displayOrder: idx + 1 }));
    const payload = { ...form, interviewQuestions };
    const nextLocal =
      edit === "new"
        ? [...local, { ...payload, id: "j-" + Date.now() }]
        : local.map((j) => (j.id === edit ? { ...j, ...payload } : j));
    setLocal(nextLocal);
    setEdit(null);
    setQErrors({});
    setToast("Saving…");
    setSaving(true);
    const res = await persistNow(nextLocal);
    setSaving(false);
    if (res?.ok) {
      setToast(`Saved: ${openN} opening · ${roleN} role · ${closeN} closing.`);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } else {
      setToast(res?.error || "Could not save to server. Please try again.");
    }
    setTimeout(() => setToast(""), 4000);
  };
  const applyPositionStatus = async (nextOpen) => {
    if (!editingJob?.id) return;
    setPositionConfirm(null);
    setPositionToggling(true);
    try {
      const res = await fetch(
        `/api/jobs/${encodeURIComponent(editingJob.id)}/status`,
        apiFetchInit({
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isOpen: nextOpen }),
        }),
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setToast(data.error || `Could not update position (${res.status}).`);
        setTimeout(() => setToast(""), 4000);
        return;
      }
      const nextLocal = local.map((j) =>
        j.id === editingJob.id
          ? {
              ...j,
              isOpen: data.job?.isOpen !== false,
              closedAt: data.job?.closedAt,
            }
          : j,
      );
      setLocal(nextLocal);
      onSave(nextLocal);
      setToast(nextOpen ? "Position re-opened." : "Position closed.");
      setTimeout(() => setToast(""), 4000);
    } catch (_e) {
      setToast("Network error — could not update position.");
      setTimeout(() => setToast(""), 4000);
    } finally {
      setPositionToggling(false);
    }
  };
  const togglePositionStatus = () => {
    if (!editingJob?.id) return;
    setPositionConfirm({ nextOpen: !positionOpen });
  };
  const moveRow = (from, to) => {
    if (to < 0 || to >= questionRows.length) return;
    setQuestionRows((rows) => {
      const next = [...rows];
      const [x] = next.splice(from, 1);
      next.splice(to, 0, x);
      return next;
    });
  };
  const addRow = (phase) => {
    const cap = phaseCap(phase);
    const filled = questionRows.filter((r) => r.questionPhase === phase && r.question.trim()).length;
    if (filled >= cap) return;
    setQuestionRows((rows) => {
      const next = [...rows];
      let insertAt;
      if (phase === "mandatory_open") {
        const firstRole = next.findIndex((r) => r.questionPhase === "role");
        const firstClose = next.findIndex((r) => r.questionPhase === "mandatory_close");
        if (firstRole >= 0) insertAt = firstRole;
        else if (firstClose >= 0) insertAt = firstClose;
        else insertAt = next.length;
      } else if (phase === "mandatory_close") {
        insertAt = next.length;
      } else {
        const closeIdx = next.findIndex((r) => r.questionPhase === "mandatory_close");
        insertAt = closeIdx >= 0 ? closeIdx : next.length;
      }
      next.splice(insertAt, 0, { question: "", questionType: "open_ended", questionPhase: phase });
      return next;
    });
  };
  const countJobQuestions = (j) => {
    const iq = j.interviewQuestions || [];
    const ok = (q) => q.question && String(q.question).trim().length >= 10;
    const open = iq.filter((q) => q.questionPhase === "mandatory_open" && ok(q)).length;
    const role = iq.filter((q) => (q.questionPhase === "role" || !q.questionPhase) && ok(q)).length;
    const close = iq.filter((q) => q.questionPhase === "mandatory_close" && ok(q)).length;
    return { open, role, close };
  };
  const renderQuestionSection = (phaseFilter, sectionTitle, hint, allowRemove) => {
    const indices = questionRows.map((r, i) => ({ r, i })).filter(({ r }) => r.questionPhase === phaseFilter);
    const filledForPhase = indices.filter(({ r }) => r.question.trim()).length;
    const capForPhase = phaseCap(phaseFilter);
    const labelForPhase = phaseLabel(phaseFilter);
    return (
      <div key={phaseFilter} className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h4 className="text-xs font-black text-ink-600 uppercase tracking-wide">{sectionTitle}</h4>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-50 text-brand-700">{phaseFilter === "role" ? "HR script" : "Mandatory"}</span>
        </div>
        {hint ? <p className="text-xs text-ink-400 mb-3">{hint}</p> : null}
        <div className="space-y-2">
          {indices.map(({ r: row, i }, localIdx) => (
            <div key={i} className={`flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-start gap-2 ${qErrors[i] ? "rounded-xl ring-2 ring-red-300 bg-red-50/50 p-2 -mx-2" : ""}`}>
              <div className="flex items-start gap-2 shrink-0">
                <div className="flex flex-col gap-0.5 pt-2 shrink-0">
                  <button type="button" disabled={localIdx === 0} onClick={() => moveRow(i, indices[localIdx - 1].i)} className="text-ink-400 hover:text-ink-600 disabled:opacity-30 text-xs leading-none" aria-label="Move up">↑</button>
                  <button type="button" disabled={localIdx >= indices.length - 1} onClick={() => moveRow(i, indices[localIdx + 1].i)} className="text-ink-400 hover:text-ink-600 disabled:opacity-30 text-xs leading-none" aria-label="Move down">↓</button>
                </div>
                <span className="text-xs font-bold text-ink-400 pt-3 w-8 shrink-0">Q{localIdx + 1}</span>
              </div>
              <Input value={row.question} placeholder={phaseFilter === "role" ? "e.g. Walk me through your most recent IVF lab experience" : "Interview question"} onChange={(e) => { const v = e.target.value; setQuestionRows((rows) => rows.map((r, j) => j === i ? { ...r, question: v } : r)); setQErrors((e0) => { const n = { ...e0 }; delete n[i]; return n; }); }} className="w-full sm:flex-1 sm:min-w-[200px]"/>
              <div className="w-full sm:w-32 sm:shrink-0">
                <Select value={row.questionType} onChange={(e) => setQuestionRows((rows) => rows.map((r, j) => j === i ? { ...r, questionType: e.target.value } : r))} className="w-full">
                  <option value="open_ended">Open-ended</option>
                  <option value="yes_no">Yes / No</option>
                  <option value="scale_1_5">Scale 1–5</option>
                </Select>
              </div>
              {allowRemove ? (
                <button type="button" disabled={indices.length <= 1} onClick={() => setQuestionRows((rows) => rows.filter((_, j) => j !== i))} className="text-ink-400 hover:text-red-500 disabled:opacity-30 text-lg px-1 shrink-0" aria-label="Remove">✕</button>
              ) : null}
              {qErrors[i] ? <p className="w-full text-xs text-red-600 font-semibold sm:pl-20">{qErrors[i]}</p> : null}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          {filledForPhase < capForPhase ? (
            <button type="button" onClick={() => addRow(phaseFilter)} className="text-sm font-bold text-brand-600 hover:text-brand-700">+ Add {labelForPhase} question</button>
          ) : (
            <span className="text-xs text-amber-700 font-semibold">Maximum {capForPhase} {labelForPhase} questions.</span>
          )}
          <span className="text-xs text-ink-400">{labelForPhase.charAt(0).toUpperCase() + labelForPhase.slice(1)} questions: {filledForPhase} / {capForPhase}</span>
        </div>
      </div>
    );
  };
  return (
    <div className="min-h-dvh safe-bottom bg-surface-alt">
      <div className="bg-white border-b border-line text-ink-900 px-4 sm:px-6 py-3 sm:py-4 safe-top safe-x flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3"><button type="button" onClick={async () => { await persistNow(local); onBack(); }} className="text-ink-400 hover:text-ink-900">← Back</button><span className="font-bold">Job Master</span></div>
        <Button type="button" className="w-full sm:w-auto" loading={saving} onClick={async () => { setSaving(true); setToast("Saving…"); const res = await persistNow(local); setSaving(false); if (res?.ok) { setSaved(true); setToast("All changes saved."); setTimeout(() => setSaved(false), 2000); } else { setToast(res?.error || "Could not save to server. Please try again."); } setTimeout(() => setToast(""), 4000); }} style={saved ? { backgroundColor: "rgb(var(--ok-rgb))" } : undefined}>{saved ? "✓ Saved" : "Save"}</Button>
      </div>
      {toast ? <div className="bg-teal-600 text-white text-center text-sm py-2 font-semibold">{toast}</div> : null}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-x">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h1 className="text-xl sm:text-2xl font-black text-ink-900">Job Postings</h1>
          <Button type="button" className="w-full sm:w-auto" onClick={() => { setForm({ title: "", designation: "", location: "", description: "", requirements: "" }); setQuestionRows(defaultNewQuestionRows()); setQErrors({}); setEdit("new"); }}>+ New</Button>
        </div>
        <div className="mb-6">
          <Input
            type="search"
            value={jobSearch}
            onChange={(e) => setJobSearch(e.target.value)}
            placeholder="Search by title, designation, or location…"
            aria-label="Search job postings"
            className="w-full"
          />
        </div>
        {edit && (
          <div ref={editorRef} className="scroll-mt-24 bg-white rounded-2xl border border-brand-200 shadow-lg p-4 sm:p-6 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">{[["title", "Title"], ["designation", "Designation"], ["location", "Location"]].map(([k, l]) => <Field key={k} label={l}><Input value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} /></Field>)}</div>
            {[["description", "Description"], ["requirements", "Requirements"]].map(([k, l]) => <div key={k} className="mb-4"><Field label={l}><Textarea value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} rows={3} /></Field></div>)}
            <div className="border-t border-line pt-5 mt-2 mb-4">
              <h3 className="text-sm font-black text-ink-900 uppercase tracking-wide mb-1">Interview Questions</h3>
              {renderQuestionSection("mandatory_open", "Mandatory opening", "Asked first. Placeholders: {{candidateName}}, {{jobTitle}}, {{companyName}}.", true)}
              {renderQuestionSection("role", "Role-specific (HR script)", "Asked after opening, before AI follow-ups.", true)}
              {renderQuestionSection("mandatory_close", "Mandatory closing", "Asked last after AI follow-ups.", true)}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="button" className="w-full sm:w-auto" loading={saving} onClick={saveJob}>Save</Button>
              <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => setEdit(null)}>Cancel</Button>
              {edit && edit !== "new" ? (
                <Button
                  type="button"
                  variant="secondary"
                  className={`w-full sm:w-auto${positionOpen ? "" : " opacity-60"}`}
                  loading={positionToggling}
                  onClick={togglePositionStatus}
                >
                  {positionOpen ? "Close this position" : "Open this position"}
                </Button>
              ) : null}
            </div>
          </div>
        )}
        <div className="space-y-4">
          {filteredJobs.length === 0 ? (
            <p className="text-sm text-ink-400 text-center py-8">
              {jobSearch.trim()
                ? `No jobs match “${jobSearch.trim()}”.`
                : "No job postings yet."}
            </p>
          ) : (
            filteredJobs.map((j) => (
              <div key={j.id} className="bg-white rounded-xl border border-line p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-ink-900">{j.title}</h3>
                    {j.isOpen === false ? (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-ink-500 bg-surface-alt border border-line px-2 py-0.5 rounded-full">
                        Position closed
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-ink-400">{j.designation} · {j.location}</p>
                  {(() => {
                    const c = countJobQuestions(j);
                    return c.open + c.role + c.close > 0 ? (
                      <p className="text-xs text-teal-700 font-semibold mt-1">
                        {c.open} opening · {c.role} role · {c.close} closing
                      </p>
                    ) : null;
                  })()}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:ml-4 shrink-0 w-full sm:w-auto">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      setForm({
                        title: j.title,
                        designation: j.designation || "",
                        location: j.location || "",
                        description: j.description || "",
                        requirements: j.requirements || "",
                      });
                      setQuestionRows(normalizeRowsFromJob(j));
                      setQErrors({});
                      setEdit(j.id);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    className="w-full sm:w-auto"
                    loading={removingId === j.id}
                    onClick={() => removeJob(j)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {positionConfirm ? (
        <Modal
          title={positionConfirm.nextOpen ? "Re-open position?" : "Close position?"}
          onClose={positionToggling ? undefined : () => setPositionConfirm(null)}
        >
          <p className="text-sm text-ink-600 mb-6">
            {positionConfirm.nextOpen
              ? "Re-open this position? Candidates will be able to apply again."
              : "Close this position? New applications will be blocked. Candidates already in progress can continue their interview."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={positionToggling}
              onClick={() => setPositionConfirm(null)}
              className="w-full sm:flex-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={positionConfirm.nextOpen ? "primary" : "danger"}
              loading={positionToggling}
              onClick={() => void applyPositionStatus(positionConfirm.nextOpen)}
              className="w-full sm:flex-1"
            >
              {positionConfirm.nextOpen ? "Re-open" : "Close position"}
            </Button>
          </div>
        </Modal>
      ) : null}
      {removeConfirm ? (
        <Modal
          title="Remove job?"
          onClose={removingId ? undefined : () => setRemoveConfirm(null)}
        >
          <p className="text-sm text-ink-600 mb-6">
            Remove &ldquo;{removeConfirm.label}&rdquo;? This will hide it from the careers page for candidates immediately.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={Boolean(removingId)}
              onClick={() => setRemoveConfirm(null)}
              className="w-full sm:flex-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={removingId === removeConfirm.job.id}
              onClick={() => void confirmRemoveJob(removeConfirm.job, removeConfirm.label)}
              className="w-full sm:flex-1"
            >
              Remove
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

